import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Header from '../layout/Header';
import LeadersSection from '../components/LeadersSection';
import GouvernanceCEModal from '../components/GouvernanceCEModal';
import DfkFacturesAnalysisModal from '../components/DfkFacturesAnalysisModal';
import { BarChart3, CalendarDays, CheckCircle2, Circle, Layers, ShieldCheck, Sparkles, TrendingUp, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export default function ComiteCE() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isGouvernanceCEOpen, setIsGouvernanceCEOpen] = useState(false);
  const [isDfkFacturesOpen, setIsDfkFacturesOpen] = useState(false);

  const emailsAutorisesCE = ['dany.bourassa@bzinc.ca', 'dany.bourassa@aikub.ai', 'dany.bourassa@bouinvest.com', 'eric.durand@bzinc.ca', 'francois.delisle@bzinc.ca'];

  useEffect(() => {
    if (!user?.email || !emailsAutorisesCE.includes(user.email.toLowerCase())) {
      navigate('/comites');
    }
  }, [user, navigate]);

  if (!user?.email || !emailsAutorisesCE.includes(user.email.toLowerCase())) {
    return null;
  }

  const leaders = [
    {
      name: 'Dany Bourassa',
      title: 'Président & DG • CA/CE',
      avatar: '/DB.png',
      vision:
        "Dany assume CA/CE avec une ligne de décision simple : les gestionnaires et directeurs lui remontent directement les enjeux transversaux, les arbitrages, les priorités et les risques.",
      attentes:
        "Dany s'attend à des décisions rapides, documentées et actionnables. Les gestionnaires gardent leurs responsabilités opérationnelles, mais les arbitrages stratégiques et transversaux reviennent directement à Dany CA/CE.",
      pagePRIsME:
        'Cette page conserve uniquement le cadre de gouvernance CE. Les anciennes cartes CE sensibles ou mal positionnées ont été retirées de PRIsME Master.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Header
        title="Dany CA/CE"
        subtitle="Cadre de gouvernance exécutive BZ : structure, responsabilités, organigramme, RACI et rituels."
      />

      <div className="mx-auto max-w-7xl px-8 pb-12">
        <LeadersSection leaders={leaders} title="Dany CA/CE" />

        <div className="mb-12 grid grid-cols-1 gap-6 md:grid-cols-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-xl border-l-4 border-indigo-500 bg-white p-6 shadow-md"
          >
            <div className="mb-2 flex items-center gap-3">
              <BarChart3 className="h-5 w-5 text-indigo-600" />
              <p className="text-sm text-gray-600">Surface conservée</p>
            </div>
            <p className="text-3xl font-bold text-gray-900">3</p>
            <p className="mt-1 text-xs text-gray-500">Gouvernance, atelier, analyse DFK</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="rounded-xl border-l-4 border-green-500 bg-white p-6 shadow-md"
          >
            <div className="mb-2 flex items-center gap-3">
              <ShieldCheck className="h-5 w-5 text-green-600" />
              <p className="text-sm text-gray-600">Posture</p>
            </div>
            <p className="text-3xl font-bold text-gray-900">Nettoyée</p>
            <p className="mt-1 text-xs text-gray-500">Anciennes cartes CE retirées</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="rounded-xl border-l-4 border-purple-500 bg-white p-6 shadow-md"
          >
            <div className="mb-2 flex items-center gap-3">
              <Layers className="h-5 w-5 text-purple-600" />
              <p className="text-sm text-gray-600">Contenu actif</p>
            </div>
            <p className="text-3xl font-bold text-gray-900">Gouvernance</p>
            <p className="mt-1 text-xs text-gray-500">Organigramme, RACI, rituels</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="rounded-xl border-l-4 border-blue-500 bg-white p-6 shadow-md"
          >
            <div className="mb-2 flex items-center gap-3">
              <Users className="h-5 w-5 text-blue-600" />
              <p className="text-sm text-gray-600">Accès</p>
            </div>
            <p className="text-3xl font-bold text-gray-900">Restreint</p>
            <p className="mt-1 text-xs text-gray-500">Dany CA/CE seulement</p>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          whileHover={{ scale: 1.02, y: -5 }}
          className="mb-12 cursor-pointer rounded-3xl border border-indigo-200 bg-white shadow-lg transition-all duration-300 hover:shadow-xl"
          onClick={() => setIsGouvernanceCEOpen(true)}
        >
          <div className="flex flex-col gap-4 p-6 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-600/10 text-indigo-600 shadow-inner">
                <Layers className="h-6 w-6" />
              </div>
              <div>
                <h3 className="mb-1 text-xl font-bold text-gray-900">Gouvernance CE</h3>
                <p className="mb-2 text-sm text-gray-600">
                  Cadre de gouvernance exécutive du CE : esprit, organigramme, RACI, roadmap et rituels.
                </p>
                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1 rounded-full border border-indigo-100 bg-indigo-50 px-2 py-1 text-indigo-700">
                    <Circle className="h-3 w-3 fill-indigo-400 text-indigo-400" />
                    Cadre holistique
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full border border-purple-100 bg-purple-50 px-2 py-1 text-purple-700">
                    <CheckCircle2 className="h-3 w-3 text-purple-500" />
                    Alignement 2026
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.42 }}
          whileHover={{ scale: 1.01, y: -4 }}
          className="relative mb-12 cursor-pointer overflow-hidden rounded-3xl border border-amber-200 bg-gradient-to-br from-white via-amber-50 to-indigo-50 shadow-xl transition-all duration-300 hover:shadow-2xl"
          onClick={() => navigate('/comite-ce/lac-epaule-septembre-2026')}
        >
          <div className="absolute right-0 top-0 h-32 w-32 rounded-full bg-amber-300/30 blur-3xl" />
          <div className="absolute bottom-0 left-1/3 h-28 w-28 rounded-full bg-indigo-300/20 blur-3xl" />
          <div className="relative p-6 md:p-8">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-3xl">
                <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-100 px-3 py-1 text-xs font-black uppercase tracking-wide text-amber-800">
                  <CalendarDays className="h-4 w-4" />
                  Atelier stratégique · Septembre 2026
                </div>
                <h3 className="mb-2 text-3xl font-black tracking-tight text-slate-950">Lac à l’épaule — Septembre 2026</h3>
                <p className="text-sm leading-relaxed text-slate-700 md:text-base">
                  Nouveau concept CE : mur de cartes de discussion libre, sans ordre du jour rigide. Chaque sujet ouvre une modal documentée avec tags, importance, avatars, contexte enrichi, questions clés et mode atelier plein écran.
                </p>
              </div>
              <div className="grid min-w-[320px] grid-cols-3 gap-3">
                <div className="rounded-2xl border border-white bg-white/80 p-4 shadow-sm">
                  <p className="text-xs text-slate-500">Sujets</p>
                  <p className="text-2xl font-black text-slate-950">12</p>
                </div>
                <div className="rounded-2xl border border-white bg-white/80 p-4 shadow-sm">
                  <p className="text-xs text-slate-500">Participants</p>
                  <p className="text-2xl font-black text-slate-950">12</p>
                </div>
                <div className="rounded-2xl border border-white bg-white/80 p-4 shadow-sm">
                  <p className="text-xs text-slate-500">Mode</p>
                  <p className="text-2xl font-black text-slate-950">Atelier</p>
                </div>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-3 text-xs font-bold text-slate-600">
              <span className="inline-flex items-center gap-1 rounded-full bg-white/75 px-3 py-1"><Sparkles className="h-3.5 w-3.5 text-amber-500" /> Cartes éparpillées</span>
              <span className="rounded-full bg-white/75 px-3 py-1">Filtres importance / tags</span>
              <span className="rounded-full bg-white/75 px-3 py-1">Avatars gestionnaires</span>
              <span className="rounded-full bg-white/75 px-3 py-1">Frontend only</span>
            </div>
          </div>
        </motion.div>

        {/* Analyse DFK — Factures BZ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          whileHover={{ scale: 1.01, y: -4 }}
          className="relative mb-12 cursor-pointer overflow-hidden rounded-3xl border border-cyan-200 bg-gradient-to-br from-slate-950 via-blue-950 to-cyan-900 shadow-xl transition-all duration-300 hover:shadow-2xl"
          onClick={() => setIsDfkFacturesOpen(true)}
        >
          <div className="absolute inset-0 opacity-25 bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,0.6),transparent_30%),radial-gradient(circle_at_bottom_right,rgba(249,115,22,0.48),transparent_32%)]" />
          <div className="relative p-6 text-white md:p-8">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-3xl">
                <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-cyan-200/40 bg-cyan-400/15 px-3 py-1 text-xs font-black uppercase tracking-wide text-cyan-100">
                  <TrendingUp className="h-4 w-4" />
                  Analyse CE · DFK 2024-2026
                </div>
                <h3 className="mb-2 text-3xl font-black tracking-tight">DFK — progression annuelle des factures</h3>
                <p className="text-sm leading-relaxed text-cyan-50/85 md:text-base">
                  Vue exécutive prête pour la rencontre avec Marc : hausse globale du groupe BZ, variation par compagnie, contribution de BZ à l’augmentation 2026, graphiques et source Excel téléchargeable.
                </p>
              </div>
              <div className="grid min-w-[320px] grid-cols-3 gap-3">
                <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur">
                  <p className="text-xs text-cyan-100/75">Total 2026</p>
                  <p className="text-2xl font-black">192 k$</p>
                </div>
                <div className="rounded-2xl border border-orange-200/30 bg-orange-500/15 p-4 backdrop-blur">
                  <p className="text-xs text-orange-100/80">Hausse</p>
                  <p className="text-2xl font-black">+85,5%</p>
                </div>
                <div className="rounded-2xl border border-blue-200/30 bg-blue-500/15 p-4 backdrop-blur">
                  <p className="text-xs text-blue-100/80">BZ 2026</p>
                  <p className="text-2xl font-black">78,6%</p>
                </div>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-3 text-xs font-bold text-cyan-50/80">
              <span className="rounded-full bg-white/10 px-3 py-1">Graphiques annuels</span>
              <span className="rounded-full bg-white/10 px-3 py-1">Comparatif par compagnie</span>
              <span className="rounded-full bg-white/10 px-3 py-1">Source Excel intégrée</span>
              <span className="rounded-full bg-orange-400/20 px-3 py-1 text-orange-100">À présenter à Marc</span>
            </div>
          </div>
        </motion.div>
      </div>

      <GouvernanceCEModal isOpen={isGouvernanceCEOpen} onClose={() => setIsGouvernanceCEOpen(false)} />
      <DfkFacturesAnalysisModal isOpen={isDfkFacturesOpen} onClose={() => setIsDfkFacturesOpen(false)} />
    </div>
  );
}
